import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarCheck, Coins, UserCheck, UsersThree } from '@phosphor-icons/react';

import { getCoachClubNumbers } from '@/api/coach';
import { formatMoneyRounded } from '@/lib/money';
import { Skeleton } from '@/components/ui/skeleton';

// ClubNumbers — the figures a club committee asks for at a monthly meeting, in
// one panel: how many paying members and leaders, what came in this month,
// whether anyone is still unbilled, and whether attendance got confirmed.
//
// It sits below the day's work rather than above it: the KPI tiles and the to-do
// list are what an admin acts on today, these are what they report on monthly.
//
// Every figure links to the page that owns it, so a number that looks wrong is
// one click from the detail that explains it. Nothing here is a new source of
// truth — each figure is the same definition as the page it links to.
export function ClubNumbers({ slug, token }: { slug: string; token: string | null }) {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language;

  const q = useQuery({
    queryKey: ['coach-club-numbers', slug],
    queryFn: () => getCoachClubNumbers(token, slug),
    enabled: !!token && !!slug,
  });

  if (q.isLoading) {
    return (
      <section className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <Skeleton className="h-5 w-40" />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-12" />
            </div>
          ))}
        </div>
      </section>
    );
  }
  // A failed query hides the panel rather than showing an error card — the board
  // figures are a summary of pages that still work on their own.
  if (q.isError || !q.data) return null;
  const d = q.data;

  // Month label from the server's monthStart, so the heading can't disagree with
  // the figures when the browser clock is in another timezone.
  const monthLabel = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
  }).format(new Date(d.monthStart));

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-base font-semibold">{t('coach.clubNumbers.title')}</h2>
        <span className="text-xs text-foreground/55">{monthLabel}</span>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {/* Paying members, with the roster underneath — the gap between "on the
            books" and "has paid" is the point of showing them together. */}
        <Figure
          icon={<UsersThree aria-hidden weight="bold" size={16} />}
          label={t('coach.clubNumbers.payingMembers')}
          value={d.payingMembers != null ? String(d.payingMembers) : t('coach.clubNumbers.na')}
          sub={t('coach.clubNumbers.ofRoster', { count: d.rosterMembers })}
          hint={d.payingMembers == null ? t('coach.clubNumbers.noMembershipFee') : undefined}
          to={`/orgs/${slug}/members`}
        />

        <Figure
          icon={<UserCheck aria-hidden weight="bold" size={16} />}
          label={t('coach.clubNumbers.leaders')}
          value={String(d.leaders)}
          to={`/orgs/${slug}/teams`}
        />

        <Figure
          icon={<Coins aria-hidden weight="bold" size={16} />}
          label={t('coach.clubNumbers.collected')}
          value={formatMoneyRounded(d.collectedThisMonthMinor, d.currency, locale)}
          to={`/orgs/${slug}/billing`}
        />

        {/* Confirmed / unconfirmed attendance for sessions that have already
            finished. The unconfirmed half is the actionable one — it's what
            blocks an activity-subsidy claim. */}
        <Figure
          icon={<CalendarCheck aria-hidden weight="bold" size={16} />}
          label={t('coach.clubNumbers.activities')}
          value={String(d.activitiesConfirmed)}
          sub={t('coach.clubNumbers.unconfirmed', { count: d.activitiesUnconfirmed })}
          subTone={d.activitiesUnconfirmed > 0 ? 'warn' : undefined}
          to={`/orgs/${slug}/attendance`}
        />
      </div>

      {/* Unbilled members get their own row rather than a tile: it's the one
          figure here that is a problem when non-zero, and the same number the
          assistant raises in the bell. Hidden entirely when the club doesn't use
          notices (nothing to be unbilled against). */}
      {d.notNoticed != null && d.notNoticed > 0 ? (
        <Link
          to={`/orgs/${slug}/notices?tab=members`}
          className="inline-flex items-center gap-2 self-start rounded-pill border border-amber-300/70 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-900 transition hover:bg-amber-100 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-100 dark:hover:bg-amber-500/25"
        >
          {t('coach.clubNumbers.notNoticed', { count: d.notNoticed })}
          <ArrowRight aria-hidden weight="bold" size={14} />
        </Link>
      ) : null}
    </section>
  );
}

// Figure is one labelled number. The whole cell is the link target so the tap
// area clears the 44px floor on mobile without a separate button.
function Figure({
  icon,
  label,
  value,
  sub,
  subTone,
  hint,
  to,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  // `| undefined` spellings are required by exactOptionalPropertyTypes — callers
  // pass these conditionally, so the prop has to accept an explicit undefined.
  sub?: string | undefined;
  subTone?: 'warn' | undefined;
  hint?: string | undefined;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="flex min-h-[44px] flex-col gap-1 rounded-xl p-2 -m-2 transition hover:bg-muted/60"
    >
      <span className="inline-flex items-center gap-1.5 text-xs text-foreground/55">
        <span className="text-foreground/40">{icon}</span>
        {label}
      </span>
      <span className="font-heading text-xl font-bold tracking-tight">{value}</span>
      {sub ? (
        <span
          className={
            subTone === 'warn'
              ? 'text-xs font-medium text-amber-700 dark:text-amber-300'
              : 'text-xs text-foreground/50'
          }
        >
          {sub}
        </span>
      ) : null}
      {hint ? <span className="text-xs text-foreground/45">{hint}</span> : null}
    </Link>
  );
}
